<template>
<div>
    <div v-if="!app" class="text-center py-5">
        <b-spinner></b-spinner>
    </div>
    <div v-else class="fg-edit">
        <!-- Header -->
        <div class="fg-header">
            <div class="fg-header-left">
                <router-link to="/admin/forge" class="fg-back">
                    <b-icon icon="arrow-left-short" font-scale="1.4"></b-icon>
                </router-link>
                <div class="fg-title-group">
                    <h4 class="fg-title">{{ app.name }}</h4>
                </div>
                <b-badge v-if="app.status === 'failed'" variant="danger" pill class="fg-status-badge">
                    <b-icon icon="exclamation-triangle-fill" font-scale="0.8" class="mr-1"></b-icon>failed
                </b-badge>
            </div>
            <div class="fg-header-right">
                <b-button v-if="showUpdateButton" variant="warning" size="sm" @click="applyUpdate" class="mr-1">
                    <b-icon icon="arrow-up-circle-fill" class="mr-1"></b-icon>
                    {{ $t('marketplace.update') }}
                </b-button>
                <transition name="fade">
                    <span v-if="isDirty" class="fg-unsaved"></span>
                </transition>
                <b-button variant="success" size="sm" @click="save()" class="fg-save-btn">
                    <b-icon icon="check2" class="mr-1"></b-icon>{{ $t('save') }}
                    <kbd class="fg-kbd">⌘S</kbd>
                </b-button>
                <protokol-settings-dropdown size="sm">
                    <b-dropdown-item-button @click="previewForge" v-if="app.external_id === null">
                        <b-icon icon="box-arrow-up-right" class="mr-1"></b-icon> {{ $t('preview') }}
                    </b-dropdown-item-button>
                    <b-dropdown-item-button v-if="showReinstallButton" @click="reinstall">
                        <b-icon icon="arrow-clockwise" class="mr-1"></b-icon> {{ $t('reinstall') }}
                    </b-dropdown-item-button>
                    <b-dropdown-divider></b-dropdown-divider>
                    <b-dropdown-item-button @click="doCleanupApp">
                        <b-icon icon="trash" class="mr-1 text-warning"></b-icon> {{ $t('forgeCleanup') }}
                    </b-dropdown-item-button>
                    <b-dropdown-item-button v-if="canRemove" @click="remove">
                        <b-icon icon="trash" class="mr-1 text-danger"></b-icon> {{ $t('delete') }}
                    </b-dropdown-item-button>
                </protokol-settings-dropdown>
            </div>
        </div>

        <!-- Version Pipeline (internal apps only) -->
        <div class="fg-versions" v-if="app.external_id === null">
            <div class="fg-version-stage">
                <span class="fg-version-label">IDE</span>
                <b-form-select v-model="previewVersion" :options="availableVersion" size="sm" class="fg-version-select"></b-form-select>
                <b-button size="sm" variant="link" class="text-danger fg-version-action" @click="removeVersion()" v-if="app.dev_version != '' && canRemoveVersion">
                    <b-icon icon="trash"></b-icon>
                </b-button>
            </div>
            <div class="fg-version-promote">
                <b-icon icon="chevron-right" class="fg-version-arrow"></b-icon>
            </div>
            <div class="fg-version-stage">
                <span class="fg-version-label">Dev</span>
                <b-form-select v-model="devVersion" @change="publishDevVersion($event)" :options="versionOptionsWithEmpty" size="sm" class="fg-version-select">
                    <template #first>
                        <b-form-select-option :value="null" disabled>{{ devVersion }}</b-form-select-option>
                    </template>
                </b-form-select>
                <span class="fg-version-chip" :class="app.dev_version ? 'fg-version-chip--success' : 'fg-version-chip--inactive'">
                    <b-icon :icon="app.dev_version ? 'check-circle-fill' : 'dash-circle'" class="fg-version-chip-icon"></b-icon>
                    {{ app.dev_version || $t('notActive') }}
                </span>
            </div>
            <div class="fg-version-promote">
                <b-icon icon="chevron-right" class="fg-version-arrow"></b-icon>
            </div>
            <div class="fg-version-stage">
                <span class="fg-version-label">Live</span>
                <b-form-select v-model="liveVersion" @change="publishLiveVersion($event)" :options="versionOptionsWithEmpty" size="sm" class="fg-version-select">
                    <template #first>
                        <b-form-select-option :value="null" disabled>{{ liveVersion }}</b-form-select-option>
                    </template>
                </b-form-select>
                <span class="fg-version-chip" :class="app.public_version ? 'fg-version-chip--live' : 'fg-version-chip--inactive'">
                    <b-icon :icon="app.public_version ? 'globe2' : 'dash-circle'" class="fg-version-chip-icon"></b-icon>
                    {{ app.public_version || $t('notPublished') }}
                </span>
            </div>
        </div>

        <!-- Installation Error Alert -->
        <b-alert v-if="app.status === 'failed' && app.status_message" show variant="danger" class="fg-error-alert mb-0">
            <div class="d-flex align-items-start">
                <b-icon icon="exclamation-triangle-fill" class="mr-2 mt-1 flex-shrink-0"></b-icon>
                <div>
                    <strong>Installation Failed</strong>
                    <p class="mb-0 mt-1">{{ app.status_message }}</p>
                </div>
            </div>
        </b-alert>

        <!-- External app header (when no left column) -->
        <div v-if="app.external_id !== null" class="fg-external-name">
            <h5 class="mb-0">{{ app.name }}</h5>
        </div>

        <!-- Tabs -->
        <b-tabs class="fg-tabs" nav-class="fg-tab-nav" content-class="fg-tab-body">
            <!-- General Tab (not shown for service apps) -->
            <b-tab v-if="app.type !== 'service'" active>
                <template #title><b-icon icon="sliders" class="mr-1"></b-icon>{{ $t('general') }}</template>
                <div class="fg-tab-pad">
                    <div class="fg-setting" v-if="app.external_id === null">
                        <label class="fg-setting-label">{{ $t('name') }}</label>
                        <b-input disabled :value="app.name" />
                    </div>

                    <div class="fg-setting" v-if="app.external_id === null && app.type === 'platform'">
                        <label class="fg-setting-label">{{ $t('label') }}</label>
                        <Labeli18n v-model="app.label" />
                    </div>

                    <div class="fg-setting" v-if="app.type === 'platform'">
                        <label class="fg-setting-label">{{ $t('workspaces') }}</label>
                        <v-select v-model="app.workspaces" label="text" :options="workspaces" multiple :reduce="lib => lib.value" />
                        <b-alert v-if="!app.workspaces || app.workspaces.length === 0" show variant="warning" class="mt-2 mb-0 py-2 px-3">
                            <small>{{ $t('forgeAppVisibleAllWorkspaces') }}</small>
                        </b-alert>
                    </div>

                    <div class="fg-setting" v-if="app.external_id === null && app.type === 'platform'">
                        <label class="fg-setting-label">{{ $t('permissions') }}</label>
                        <small class="text-muted d-block mb-2">{{ $t('appSettingsPermissionsDescription') }}</small>
                        <div class="grants-tag-container">
                            <b-badge
                                v-for="(perm, idx) in permissions"
                                :key="idx"
                                variant="secondary"
                                class="grant-tag mr-2 mb-2"
                            >
                                {{ perm }}
                            </b-badge>
                            <small v-if="!permissions.length" class="text-muted">&mdash;</small>
                        </div>
                    </div>
                </div>
            </b-tab>

            <!-- Domains Tab (public apps only) -->
            <b-tab v-if="app.type === 'public'">
                <template #title><b-icon icon="globe2" class="mr-1"></b-icon>Domains</template>
                <div class="fg-tab-pad">
                    <div class="fg-setting">
                        <label class="fg-setting-label">{{ $t('defaultDomain') }}</label>
                        <b-input :value="app.config && app.config.domain && app.config.domain.basehostname" disabled />
                    </div>

                    <b-alert show variant="info" class="my-3">
                        <div class="d-flex align-items-start">
                            <b-icon icon="info-circle-fill" class="mr-2 mt-1 flex-shrink-0"></b-icon>
                            <div>
                                <strong>{{ $t('stageSubdomainRequired') }}</strong>
                                <p class="mb-0 mt-1">{{ $t('stageSubdomainDescription') }}</p>
                            </div>
                        </div>
                    </b-alert>

                    <div class="fg-setting">
                        <label class="fg-setting-label">{{ $t('customDomains') }}</label>
                        <small class="text-muted d-block mb-2">{{ $t('customDomainsDescription') }}</small>

                        <b-input-group class="mb-3">
                            <b-form-input
                                v-model="newDomain"
                                :placeholder="$t('enterDomainName')"
                                @keyup.enter="addDomain"
                            />
                            <b-input-group-append>
                                <b-button variant="primary" @click="addDomain">
                                    <b-icon icon="plus"></b-icon> {{ $t('add') }}
                                </b-button>
                            </b-input-group-append>
                        </b-input-group>

                        <div v-if="customDomains.length > 0" class="domain-list">
                            <div v-for="(domain, index) in customDomains" :key="index" class="fg-domain-card">
                                <div class="d-flex justify-content-between align-items-start">
                                    <div class="flex-grow-1">
                                        <div class="d-flex align-items-center mb-2">
                                            <b-icon
                                                :icon="isDomainVerified(domain) ? 'check-circle-fill' : 'exclamation-circle-fill'"
                                                :variant="isDomainVerified(domain) ? 'success' : 'warning'"
                                                class="mr-2"
                                            ></b-icon>
                                            <h6 class="mb-0">{{ getDomainName(domain) }}</h6>
                                            <span class="fg-domain-status ml-2" :class="isDomainVerified(domain) ? 'fg-domain-status--verified' : 'fg-domain-status--pending'">
                                                {{ isDomainVerified(domain) ? $t('verified') : $t('pendingVerification') }}
                                            </span>
                                        </div>

                                        <div v-if="!isDomainVerified(domain)" class="verification-info">
                                            <small class="text-muted">{{ $t('addTxtRecord') }}:</small>
                                            <b-input-group size="sm" class="mt-2">
                                                <b-input-group-prepend is-text>
                                                    <strong>{{ getDomainName(domain) }}</strong>
                                                </b-input-group-prepend>
                                                <b-form-input
                                                    :value="domainValidationKeys[getDomainName(domain)] || $t('clickRefreshToGetKey')"
                                                    readonly
                                                />
                                                <b-input-group-append>
                                                    <b-button
                                                        v-if="domainValidationKeys[getDomainName(domain)]"
                                                        variant="outline-secondary"
                                                        size="sm"
                                                        @click="copyToClipboard(domainValidationKeys[getDomainName(domain)])"
                                                    >
                                                        <b-icon icon="clipboard"></b-icon>
                                                    </b-button>
                                                    <b-button
                                                        v-if="!domainValidationKeys[getDomainName(domain)]"
                                                        variant="outline-primary"
                                                        size="sm"
                                                        @click="refreshValidationKey(domain)"
                                                    >
                                                        <b-icon icon="arrow-repeat"></b-icon>
                                                    </b-button>
                                                </b-input-group-append>
                                            </b-input-group>
                                            <b-button
                                                variant="outline-primary"
                                                size="sm"
                                                class="mt-2"
                                                @click="verifyDomain(domain)"
                                            >
                                                <b-icon icon="arrow-clockwise"></b-icon> {{ $t('verifyDomain') }}
                                            </b-button>
                                        </div>
                                    </div>
                                    <b-button
                                        variant="outline-danger"
                                        size="sm"
                                        @click="removeDomain(index)"
                                    >
                                        <b-icon icon="trash"></b-icon>
                                    </b-button>
                                </div>
                            </div>
                        </div>
                        <div v-else class="fg-empty-state">
                            <b-icon icon="globe2" font-scale="1.2" class="mr-2"></b-icon>
                            <span>{{ $t('noCustomDomainsAdded') }}</span>
                        </div>
                    </div>
                </div>
            </b-tab>

            <!-- Services Tab -->
            <b-tab v-if="activeServices.length > 0" :active="app.type === 'service'">
                <template #title><b-icon icon="gear-wide-connected" class="mr-1"></b-icon>Services</template>
                <div class="fg-tab-pad">
                    <div class="fg-setting">
                        <label class="fg-setting-label">Registered Services</label>
                        <small class="text-muted d-block mb-3">Services declared in the current version's manifest. Access scope and permissions are set at build time.</small>

                        <div class="fg-services-list">
                            <div v-for="svc in activeServices" :key="svc.name" class="fg-service-card">
                                <div class="d-flex justify-content-between align-items-start">
                                    <div class="flex-grow-1">
                                        <div class="d-flex align-items-center mb-1">
                                            <h6 class="mb-0 mr-2">{{ svc.name }}</h6>
                                            <span class="fg-access-badge" :class="svc.access === 'public' ? 'fg-access-badge--public' : 'fg-access-badge--platform'">
                                                {{ svc.access }}
                                            </span>
                                        </div>
                                        <small v-if="svc.label" class="text-muted d-block mb-1">
                                            {{ typeof svc.label === 'object' ? (svc.label.locales && svc.label.locales.en_US) || svc.label : svc.label }}
                                        </small>
                                        <div v-if="svc.permissions && svc.permissions.length" class="mt-2">
                                            <b-badge v-for="perm in svc.permissions" :key="perm" variant="secondary" class="mr-1 mb-1" pill>
                                                {{ perm }}
                                            </b-badge>
                                        </div>
                                        <small v-else class="text-muted">No permissions declared</small>
                                    </div>
                                    <div class="text-right flex-shrink-0">
                                        <code class="fg-service-script">{{ svc.script }}</code>
                                    </div>
                                </div>
                                <div v-if="app.uuid" class="mt-2 d-flex align-items-center justify-content-between">
                                    <div>
                                        <small class="text-muted">Endpoint: </small>
                                        <code class="fg-service-url">{{ serviceUrl(svc) }}</code>
                                    </div>
                                    <b-button size="sm" variant="outline-primary" @click="openServiceTest(svc)">
                                        <b-icon icon="terminal" class="mr-1"></b-icon>
                                        Test
                                    </b-button>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </b-tab>

            <!-- API User Tab (public apps with internal origin) -->
            <b-tab v-if="app.external_id === null && app.type === 'public'">
                <template #title><b-icon icon="person-badge" class="mr-1"></b-icon>{{ $t('apiUser') }}</template>
                <div class="fg-tab-pad">
                    <b-alert show variant="info" class="mb-3">
                        <div class="d-flex align-items-start">
                            <b-icon icon="info-circle-fill" class="mr-2 mt-1 flex-shrink-0"></b-icon>
                            <div><small>{{ $t('apiUserDescription') }} {{ $t('apiUserRoleDescription') }}</small></div>
                        </div>
                    </b-alert>

                    <b-alert v-if="app.type === 'public'" show variant="danger" class="mb-3">
                        <div class="d-flex align-items-start">
                            <b-icon icon="exclamation-triangle-fill" class="mr-2 mt-1 flex-shrink-0"></b-icon>
                            <div><small>{{ $t('apiUserPublicWarning') }}</small></div>
                        </div>
                    </b-alert>

                    <div v-if="app.config && app.config.api_user" class="fg-api-user-card">
                        <div class="d-flex justify-content-between align-items-start">
                            <div class="flex-grow-1">
                                <h6 class="mb-1">{{ $t('apiUser') }}</h6>
                                <small class="text-muted">{{ app.config.api_user }}</small>

                                <div v-if="apiUserRoles.length > 0" class="mt-3">
                                    <strong class="d-block mb-2">{{ $t('roles') }}:</strong>
                                    <div class="role-badges">
                                        <div
                                            v-for="role in apiUserRoles"
                                            :key="role.uuid"
                                            class="role-badge-wrapper"
                                        >
                                            <b-badge variant="primary" class="role-badge">
                                                {{ role.name }}
                                            </b-badge>
                                            <b-button
                                                variant="outline-secondary"
                                                size="sm"
                                                @click="openRoleEditModal(role)"
                                                class="ml-2"
                                            >
                                                <b-icon icon="gear"></b-icon>
                                            </b-button>
                                        </div>
                                    </div>
                                </div>
                                <div v-else class="mt-3">
                                    <small class="text-muted">{{ $t('loadingRoles') }}</small>
                                </div>
                            </div>
                            <b-button
                                variant="outline-primary"
                                size="sm"
                                @click="refreshApiUserToken"
                            >
                                <b-icon icon="arrow-repeat"></b-icon> {{ $t('refreshToken') }}
                            </b-button>
                        </div>
                    </div>
                    <div v-else class="fg-empty-state">
                        <b-icon icon="person-badge" font-scale="1.2" class="mr-2"></b-icon>
                        <span>{{ $t('noApiUserConfigured') }}</span>
                    </div>
                </div>
            </b-tab>

            <!-- Variables Tab -->
            <b-tab>
                <template #title><b-icon icon="braces" class="mr-1"></b-icon>Variables</template>
                <div class="fg-tab-pad" style="max-width: 900px">
                    <small class="text-muted d-block mb-4">User-defined variables available as <code>$variables</code> in install/uninstall scripts and via the SDK.</small>

                    <div v-for="env in ['dev', 'live']" :key="env" class="fg-var-env-section">
                        <div class="fg-var-env-header">
                            <span class="fg-var-env-label" :class="env === 'dev' ? 'fg-var-env-label--dev' : 'fg-var-env-label--live'">{{ env === 'dev' ? 'Dev' : 'Live' }}</span>
                            <small class="text-muted">{{ Object.keys((app.variables || {})[env] || {}).length }} variable(s)</small>
                        </div>
                        <div class="fg-var-list" v-if="app.variables && app.variables[env] && Object.keys(app.variables[env]).length">
                            <div v-for="(val, key) in app.variables[env]" :key="key" class="fg-var-row">
                                <code class="fg-var-key">{{ key }}</code>
                                <span class="fg-var-eq">=</span>
                                <b-input :value="app.variables[env][key]" @input="updateVariable(env, key, $event)" size="sm" class="fg-var-value" />
                                <b-button size="sm" variant="link" class="fg-var-delete text-danger p-0" @click="removeVariable(env, key)">
                                    <b-icon icon="x"></b-icon>
                                </b-button>
                            </div>
                        </div>
                        <div v-else class="fg-empty-state mb-3">
                            <b-icon icon="braces" font-scale="1.2" class="mr-2"></b-icon>
                            <span>No {{ env }} variables</span>
                        </div>
                        <div class="fg-var-add">
                            <b-input v-model="newVar[env].key" size="sm" placeholder="KEY" class="fg-var-add-key" />
                            <span class="fg-var-eq">=</span>
                            <b-input v-model="newVar[env].value" size="sm" placeholder="value" class="fg-var-add-value" @keyup.enter="addVariable(env)" />
                            <b-button size="sm" variant="outline-secondary" @click="addVariable(env)" :disabled="!newVar[env].key.trim()">
                                <b-icon icon="plus"></b-icon> Add
                            </b-button>
                        </div>
                    </div>
                </div>
            </b-tab>

            <!-- Grants Tab (manage forge permission only) -->
            <b-tab v-if="$can('manage forge')">
                <template #title><b-icon icon="shield-lock" class="mr-1"></b-icon>{{ $t('marketplace.installWizard.grants') }}</template>
                <div class="fg-tab-pad">
                    <div class="d-flex justify-content-between align-items-center mb-3">
                        <div>
                            <p class="text-muted small mb-0">{{ $t('marketplace.installWizard.grantsDescription') }}</p>
                        </div>
                        <div class="d-flex align-items-center">
                            <b-button size="sm" variant="outline-secondary" class="mr-2" @click="openGrantsDialog">
                                <b-icon icon="plus" class="mr-1"></b-icon>
                                {{ $t('choose') }}
                            </b-button>
                            <b-button size="sm" variant="primary" :disabled="savingGrants" @click="saveGrants">
                                <b-spinner v-if="savingGrants" small class="mr-1"></b-spinner>
                                {{ $t('save') }}
                            </b-button>
                        </div>
                    </div>

                    <div class="grants-badges" v-if="localGrants.length">
                        <b-badge
                            v-for="grant in localGrants"
                            :key="grant"
                            :variant="grantsIsForced(grant) ? 'danger' : 'warning'"
                            class="grant-tag mr-2 mb-2"
                        >
                            <span
                                style="cursor: pointer;"
                                :title="grantsIsForced(grant) ? $t('marketplace.installWizard.grantsForcedTitle') : $t('marketplace.installWizard.grantsRegularTitle')"
                                @click="grantsToggleForcedInList(grant)"
                            >
                                <b-icon v-if="grantsIsForced(grant)" icon="lock-fill" class="mr-1"></b-icon>
                                {{ grantsDisplayName(grant) }}
                            </span>
                            <span style="cursor: pointer;" class="ml-1" @click="grantsRemoveFromList(grant)">
                                <b-icon icon="x"></b-icon>
                            </span>
                        </b-badge>
                    </div>
                    <div v-else class="fg-empty-state">
                        <b-icon icon="shield-lock" font-scale="1.2" class="mr-2"></b-icon>
                        <span>{{ $t('marketplace.installWizard.grantsEmpty') }}</span>
                    </div>
                </div>
            </b-tab>
        </b-tabs>

        <!-- ═══ Modals ═══ -->

        <!-- Role Edit Modal -->
        <main-dialog
            v-model="roleEditModal.show"
            :title="$t('editRolePermissions')"
            size="xl"
            @hidden="resetRoleEditModal"
            hide-footer
            scrollable
        >
            <RoleEditComponent
                v-if="roleEditModal.roleId"
                :roleUuid="roleEditModal.roleId"
                @role-updated="onRoleUpdated"
            />
        </main-dialog>

        <!-- Permissions Approval Dialog -->
        <main-dialog
            :show="permDialog.show"
            :title="permDialog.title"
            :icon="app ? app.icon : null"
            @close="permDialog.show = false"
        >
            <p>{{ permDialog.leadMessage }}</p>

            <div v-if="permDialogEntitlements.length" class="iw-section">
                <div class="iw-section-title iw-section-title--entitlement">
                    <b-icon icon="star-fill" class="mr-1"></b-icon>
                    {{ $t('marketplace.installWizard.entitlements') }}
                </div>
                <p class="text-muted small mb-2">{{ $t('marketplace.installWizard.entitlementsDescription') }}</p>
                <div v-for="(perm, idx) in permDialogEntitlements" :key="'ent-'+idx" class="iw-perm-row iw-perm-row--entitlement">
                    <b-icon icon="star-fill" class="iw-perm-icon text-warning"></b-icon>
                    <code class="iw-perm-name">{{ perm }}</code>
                </div>
            </div>

            <div v-if="permDialogInstallPerms.length" class="iw-section">
                <div class="iw-section-title">
                    <b-icon icon="shield-lock" class="mr-1"></b-icon>
                    {{ $t('marketplace.installWizard.installPermissions') }}
                </div>
                <p class="text-muted small mb-3">{{ $t('marketplace.installWizard.installPermissionsDescription') }}</p>
                <div v-for="(perm, idx) in permDialogInstallPerms" :key="'ip-'+idx" class="iw-perm-row">
                    <b-icon icon="shield-check" class="iw-perm-icon text-muted"></b-icon>
                    <div>
                        <code class="iw-perm-name">{{ perm.permission || perm }}</code>
                        <small v-if="perm.reason" class="d-block text-muted mt-1">{{ perm.reason }}</small>
                    </div>
                </div>
            </div>

            <div v-if="permDialogRuntimePerms.length" class="iw-section">
                <div class="iw-section-title">
                    <b-icon icon="cpu" class="mr-1"></b-icon>
                    {{ $t('marketplace.installWizard.runtimePermissions') }}
                </div>
                <p class="text-muted small mb-2">{{ $t('marketplace.installWizard.runtimePermissionsDescription') }}</p>
                <div v-for="(perm, idx) in permDialogRuntimePerms" :key="'rp-'+idx" class="iw-perm-row">
                    <b-icon icon="lightning-fill" class="iw-perm-icon text-muted"></b-icon>
                    <code class="iw-perm-name">{{ perm }}</code>
                </div>
            </div>

            <div v-if="!permDialogInstallPerms.length && !permDialogRuntimePerms.length && !permDialogEntitlements.length" class="text-center py-2">
                <b-icon icon="check-circle-fill" variant="success" font-scale="1.5"></b-icon>
                <p class="mt-2 mb-0 text-muted small">{{ $t('marketplace.updateDialog.noPermissionChanges') }}</p>
            </div>

            <template #footer>
                <b-button variant="outline-secondary" @click="permDialog.show = false">{{ $t('cancel') }}</b-button>
                <b-button variant="primary" @click="executePermDialogAction">{{ $t('approve') }}</b-button>
            </template>
        </main-dialog>

        <!-- Grants Picker Dialog -->
        <main-dialog
            :show="grantsDialogShow"
            :title="$t('marketplace.installWizard.grants')"
            maxWidth="1200px"
            @close="grantsDialogShow = false"
        >
            <b-form-input
                v-model="grantsFilter"
                size="sm"
                :placeholder="$t('search')"
                class="mb-3"
            ></b-form-input>

            <div class="grants-tabs-container mb-3">
                <button class="grants-scroll-btn" @click="grantsScrollTabs('left')" v-show="grantsShowLeftArrow">
                    <b-icon icon="chevron-left"></b-icon>
                </button>
                <div class="grants-tabs-wrapper" ref="grantsTabsWrapper" @scroll="grantsHandleTabScroll">
                    <b-tabs v-model="grantsActiveTab" content-class="mt-3">
                        <b-tab v-for="(permissions, group) in grantsFilteredPermissionsList" :key="group">
                            <template #title>
                                <span class="tab-title">
                                    {{ grantsFormatGroupName(group) }}
                                    <b-badge variant="light" class="ml-2">{{ grantsGetGroupGrantCount(group) }}</b-badge>
                                </span>
                            </template>
                            <div class="grants-components-grid">
                                <div
                                    v-for="(permList, component) in permissions"
                                    :key="component"
                                    class="grants-component-card"
                                    :class="{ 'has-selected': grantsDraftHasSelectedPerms(group, component) }"
                                >
                                    <div class="grants-component-header" @click="grantsDraftToggleAllComponentPerms(group, component, permList)">
                                        <div class="grants-component-info">
                                            <h6 class="grants-component-name">{{ grantsFormatComponentName(component) }}</h6>
                                            <span class="grants-component-count">{{ grantsDraftGetSelectedCount(group, component, permList) }}/{{ permList.length }}</span>
                                        </div>
                                        <b-form-checkbox
                                            :checked="grantsDraftAreAllSelected(group, component, permList)"
                                            :indeterminate="grantsDraftIsSomeSelected(group, component, permList)"
                                            @change="grantsDraftToggleAllComponentPerms(group, component, permList)"
                                        ></b-form-checkbox>
                                    </div>
                                    <div class="grants-permissions-list">
                                        <div
                                            v-for="perm in permList"
                                            :key="perm"
                                            class="grants-permission-item"
                                            @click="grantsDraftTogglePerm(perm, component)"
                                        >
                                            <b-form-checkbox
                                                :checked="grantsDraftIsPermSelected(perm, component)"
                                                @change="grantsDraftTogglePerm(perm, component)"
                                            >
                                                <span class="permission-label">{{ grantsFormatPermissionName(perm) }}</span>
                                            </b-form-checkbox>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </b-tab>
                    </b-tabs>
                </div>
                <button class="grants-scroll-btn" @click="grantsScrollTabs('right')" v-show="grantsShowRightArrow">
                    <b-icon icon="chevron-right"></b-icon>
                </button>
            </div>

            <div class="grants-selected-summary" v-if="grantsDraft.length > 0">
                <div class="d-flex justify-content-between align-items-center mb-2">
                    <strong>{{ $t('selectedPermissions') }} ({{ grantsDraft.length }})</strong>
                    <b-button variant="outline-danger" size="sm" @click="grantsDraft = []">{{ $t('clearAll') }}</b-button>
                </div>
                <div class="grants-badges">
                    <b-badge
                        v-for="grant in grantsDraft"
                        :key="grant"
                        :variant="grantsIsForced(grant) ? 'danger' : 'warning'"
                        class="grant-tag mr-2 mb-2"
                    >
                        <span
                            style="cursor: pointer;"
                            :title="grantsIsForced(grant) ? $t('marketplace.installWizard.grantsForcedTitle') : $t('marketplace.installWizard.grantsRegularTitle')"
                            @click="grantsDraftToggleForcedInList(grant)"
                        >
                            <b-icon v-if="grantsIsForced(grant)" icon="lock-fill" class="mr-1"></b-icon>
                            {{ grantsDisplayName(grant) }}
                        </span>
                        <span style="cursor: pointer;" class="ml-1" @click="grantsDraftRemoveFromList(grant)">
                            <b-icon icon="x"></b-icon>
                        </span>
                    </b-badge>
                </div>
            </div>

            <template #footer>
                <b-button variant="outline-secondary" @click="grantsDialogShow = false">{{ $t('cancel') }}</b-button>
                <b-button variant="primary" @click="grantsDialogApply">{{ $t('apply') }}</b-button>
            </template>
        </main-dialog>

        <!-- Cleanup Confirmation Dialog -->
        <main-dialog
            :show="cleanupDialog.show"
            :title="$t('forgeCleanup')"
            :icon="app ? app.icon : null"
            @close="cleanupDialog.show = false"
        >
            <b-alert show variant="warning" class="mb-3">
                <div class="d-flex align-items-start">
                    <b-icon icon="exclamation-triangle-fill" class="mr-2 mt-1 flex-shrink-0"></b-icon>
                    <div>{{ $t('forgeCleanupNotice') }}</div>
                </div>
            </b-alert>
            <p class="mb-0">{{ $t('forgeConfirmCleanup') }}</p>

            <template #footer>
                <b-button variant="outline-secondary" @click="cleanupDialog.show = false">{{ $t('cancel') }}</b-button>
                <b-button variant="warning" :disabled="cleaningUp" @click="executeCleanupApp">
                    <b-spinner v-if="cleaningUp" small class="mr-1"></b-spinner>
                    {{ $t('forgeCleanup') }}
                </b-button>
            </template>
        </main-dialog>
    </div>

    <!-- Service Test Dialog -->
    <b-modal
        v-model="serviceTest.show"
        :title="'Test Service: ' + (serviceTest.service ? serviceTest.service.name : '')"
        size="lg"
        no-close-on-backdrop
        scrollable
    >
        <div v-if="serviceTest.service">
            <div class="mb-2">
                <code class="fg-service-url">{{ serviceTest.service ? serviceUrl(serviceTest.service) : '' }}</code>
                <span class="ml-2">
                    <span class="fg-access-badge" :class="serviceTest.service.access === 'public' ? 'fg-access-badge--public' : 'fg-access-badge--platform'">
                        {{ serviceTest.service.access }}
                    </span>
                </span>
            </div>

            <b-tabs small content-class="mt-3">
                <!-- Body Tab -->
                <b-tab title="Body" active>
                    <b-form-textarea
                        v-model="serviceTest.body"
                        rows="8"
                        class="font-monospace"
                        style="font-size: 0.85rem"
                        placeholder='{ "key": "value" }'
                    ></b-form-textarea>
                </b-tab>

                <!-- Query Tab -->
                <b-tab title="Query">
                    <div v-for="(param, idx) in serviceTest.queryParams" :key="idx" class="d-flex mb-2 align-items-center">
                        <b-input v-model="param.key" placeholder="Key" size="sm" class="mr-2" style="max-width: 200px" />
                        <b-input v-model="param.value" placeholder="Value" size="sm" class="flex-grow-1 mr-2" />
                        <b-button size="sm" variant="outline-danger" @click="serviceTest.queryParams.splice(idx, 1)">
                            <b-icon icon="x"></b-icon>
                        </b-button>
                    </div>
                    <b-button size="sm" variant="outline-secondary" @click="serviceTest.queryParams.push({ key: '', value: '' })">
                        <b-icon icon="plus" class="mr-1"></b-icon>Add Parameter
                    </b-button>
                </b-tab>

                <!-- Headers Tab -->
                <b-tab title="Headers">
                    <div v-for="(header, idx) in serviceTest.headers" :key="idx" class="d-flex mb-2 align-items-center">
                        <b-input v-model="header.key" placeholder="Header name" size="sm" class="mr-2" style="max-width: 200px" />
                        <b-input v-model="header.value" placeholder="Value" size="sm" class="flex-grow-1 mr-2" />
                        <b-button size="sm" variant="outline-danger" @click="serviceTest.headers.splice(idx, 1)">
                            <b-icon icon="x"></b-icon>
                        </b-button>
                    </div>
                    <b-button size="sm" variant="outline-secondary" @click="serviceTest.headers.push({ key: '', value: '' })">
                        <b-icon icon="plus" class="mr-1"></b-icon>Add Header
                    </b-button>
                </b-tab>
            </b-tabs>

            <!-- Response -->
            <div v-if="serviceTest.output !== null" class="mt-3">
                <div class="d-flex align-items-center mb-2">
                    <label class="small font-weight-bold mb-0">Response</label>
                    <span class="ml-2 small font-weight-bold" :class="serviceTest.status < 400 ? 'text-success' : 'text-danger'">
                        {{ serviceTest.status }}
                    </span>
                    <small v-if="serviceTest.time" class="text-muted ml-2">{{ serviceTest.time }}ms</small>
                </div>
                <pre class="mb-0 p-3 bg-dark text-light rounded" style="font-size: 0.8rem; max-height: 350px; overflow: auto; white-space: pre-wrap;">{{ serviceTest.output }}</pre>
            </div>
        </div>

        <template #modal-footer>
            <div class="d-flex w-100 justify-content-between align-items-center">
                <div>
                    <small v-if="serviceTest.output !== null" class="text-muted cursor-pointer" @click="serviceTest.output = null; serviceTest.status = null; serviceTest.time = null">
                        Clear response
                    </small>
                </div>
                <div>
                    <b-button variant="secondary" @click="serviceTest.show = false" class="mr-2">Close</b-button>
                    <b-button variant="success" @click="runServiceTest(serviceTest.service)" :disabled="serviceTest.loading">
                        <b-spinner small v-if="serviceTest.loading" class="mr-1"></b-spinner>
                        <b-icon v-else icon="play-fill" class="mr-1"></b-icon>
                        {{ serviceTest.loading ? 'Running...' : 'Send Request' }}
                    </b-button>
                </div>
            </div>
        </template>
    </b-modal>
</div>
</template>

<script>
import Labeli18n from '@/components/form/Texti18n'
import VSelect from "vue-select"
import { set, cloneDeep, get } from 'lodash'
import RoleEditComponent from '@/views/users/role/Edit.vue'
import { openForgeAppWithToken } from '@/helpers/forgeTokenLaunch'

export default {
    name: 'List',
    components: {
        Labeli18n,
        VSelect,
        RoleEditComponent,
    },
    props: {
        appUuid: {
            type: String,
            default: null
        }
    },
    data: function() {
        return {
            app: null,
            hostname: '',
            ideVersion: this.previewVersion,
            forkModal: false,
            forkTo: null,
            domain: null,
            uploadFile: null,
            currentEnv: 'dev',
            newDomain: '',
            domainValidationKeys: {},
            apiUserRoles: [],
            isDirty: false,
            modelLoaded: false,
            roleEditModal: {
                show: false,
                roleId: null
            },
            permDialog: {
                show: false,
                title: '',
                leadMessage: '',
                version: null,
                action: null
            },
            localGrants: [],
            savingGrants: false,
            grantsDialogShow: false,
            grantsDraft: [],
            grantsFilter: '',
            grantsActiveTab: 0,
            grantsShowLeftArrow: false,
            grantsShowRightArrow: false,
            cleaningUp: false,
            cleanupDialog: { show: false },
            newVar: {
                dev: { key: '', value: '' },
                live: { key: '', value: '' }
            },
            serviceTest: {
                show: false,
                service: null,
                body: '{}',
                queryParams: [],
                headers: [],
                output: null,
                loading: false,
                status: null,
                time: null,
            }
        }
    },
    computed: {
        uuid() {
            return this.appUuid || this.$route.params.id
        },

        availableVersion() {
            const settings = this.app.settings || []
            return settings.map(s => s.version)
        },

        versionOptionsWithEmpty() {
            return [
                { value: '', text: this.$t('deactivate') },
                ...this.availableVersion.map(v => ({ value: v, text: v }))
            ]
        },

        canRemoveVersion() {
            return this.app.settings.length > 1
        },

        workspaces() {
            return get(this.$store.getters.getCurrentProject, 'workspaces', []).map(w => {
                return {
                    text: w.name,
                    value: w.uuid
                }
            })
        },

        previewVersion: {
            get() {
                return this.app.dev_version || this.app.settings[0].version
            },
            set(val) {
                this.ideVersion = val
            }
        },

        liveVersion: {
            get() {
                return this.app.public_version || this.$t('notPublished')
            },
            set(val) {}
        },

        devVersion: {
            get() {
                return this.app.dev_version || this.$t('notActive')
            },
            set(val) {}
        },

        parseHostname: {
            get() {
                return this.hostname.replace(/[^a-z -]/g, '').replaceAll(' ', '');
            },
            set(val) {
                this.hostname = val.replace(/[^a-z -]/g, '').replaceAll(' ', '');
            }
        },

        canRemove() {
            if (!this.app.integrators) return true
            return this.app.integrators.indexOf("platform") !== -1
        },

        permDialogInstallPerms() {
            var version = this.permDialog.version
            if (!version || !this.app || !this.app.settings) return []
            var vs = null
            for (var i = 0; i < this.app.settings.length; i++) {
                if (this.app.settings[i].version === version) { vs = this.app.settings[i]; break }
            }
            return (vs && vs.install_permissions) || []
        },

        permDialogRuntimePerms() {
            var version = this.permDialog.version
            if (!version || !this.app || !this.app.settings) return []
            var vs = null
            for (var i = 0; i < this.app.settings.length; i++) {
                if (this.app.settings[i].version === version) { vs = this.app.settings[i]; break }
            }
            return (vs && vs.runtime_permissions) || []
        },

        permDialogEntitlements() {
            var version = this.permDialog.version
            if (!version || !this.app || !this.app.settings) return []
            var vs = null
            for (var i = 0; i < this.app.settings.length; i++) {
                if (this.app.settings[i].version === version) { vs = this.app.settings[i]; break }
            }
            return (vs && vs.entitlements) || []
        },

        settings() {
            return this.app.settings.find(s => {
                return s.version == this.ideVersion
            })
        },

        permissions() {
            const { permissions = [] } = this.settings || {}
            return permissions
        },

        activeServices() {
            if (!this.settings || !this.settings.services) return []
            return this.settings.services
        },

        grantsPermissionsList() {
            return this.$store.getters.permissionsList || {}
        },

        grantsFilteredPermissionsList() {
            var rawList = this.grantsPermissionsList
            var searchLower = this.grantsFilter ? this.grantsFilter.toLowerCase() : null
            var filtered = {}
            var groups = Object.keys(rawList)
            for (var i = 0; i < groups.length; i++) {
                var groupKey = groups[i]
                var groupVal = rawList[groupKey] || {}
                var filteredGroup = {}
                var components = Object.keys(groupVal)
                for (var j = 0; j < components.length; j++) {
                    var component = components[j]
                    var perms = groupVal[component]
                    if (!perms) continue
                    if (!searchLower) {
                        filteredGroup[component] = perms
                        continue
                    }
                    var matching = []
                    for (var k = 0; k < perms.length; k++) {
                        var fullStr = (perms[k] + ' ' + component).toLowerCase()
                        var formatted = perms[k].replace('function::', '').replace(/::/g, ' ').replace(/_/g, ' ').toLowerCase()
                        if (fullStr.indexOf(searchLower) !== -1 || formatted.indexOf(searchLower) !== -1) {
                            matching.push(perms[k])
                        }
                    }
                    if (matching.length > 0) filteredGroup[component] = matching
                }
                if (Object.keys(filteredGroup).length > 0) filtered[groupKey] = filteredGroup
            }
            return filtered
        },

        customDomains: {
            get() {
                return get(this.app, 'config.domain.domains', [])
            },
            set(val) {
                if (!this.app.config) this.app.config = {}
                if (!this.app.config.domain) this.app.config.domain = {}
                this.app.config.domain.domains = val
            }
        },

        showUpdateButton() {
            return !!(this.app && this.app.update_available)
        },

        showReinstallButton() {
            if (!this.app || !this.app.settings) return false
            if (this.app.update_available) return false
            const ideVersionMatchesDeployment = this.previewVersion === this.app.dev_version || this.previewVersion === this.app.public_version
            if (!ideVersionMatchesDeployment) return false
            const versionSettings = this.app.settings.find(s => s.version === this.previewVersion)
            return versionSettings && versionSettings.scripts && versionSettings.scripts.install
        },
    },
    methods: {
        openServiceTest(svc) {
            this.serviceTest.service = svc
            this.serviceTest.body = '{}'
            this.serviceTest.queryParams = []
            this.serviceTest.headers = []
            this.serviceTest.output = null
            this.serviceTest.status = null
            this.serviceTest.time = null
            this.serviceTest.show = true
        },
        async runServiceTest(svc) {
            this.serviceTest.loading = true
            this.serviceTest.output = null
            this.serviceTest.status = null
            this.serviceTest.time = null

            let body = {}
            try {
                body = JSON.parse(this.serviceTest.body || '{}')
            } catch {
                this.serviceTest.output = 'Invalid JSON body'
                this.serviceTest.status = 400
                this.serviceTest.loading = false
                return
            }

            let url = this.serviceUrl(svc)
            const query = this.serviceTest.queryParams
                .filter(p => p.key)
                .map(p => `${encodeURIComponent(p.key)}=${encodeURIComponent(p.value)}`)
                .join('&')
            if (query) url += (url.includes('?') ? '&' : '?') + query

            const headers = {}
            this.serviceTest.headers.filter(h => h.key).forEach(h => { headers[h.key] = h.value })

            const start = Date.now()
            try {
                const resp = await this.$http.post(url, body, Object.keys(headers).length ? { headers } : undefined)
                this.serviceTest.time = Date.now() - start
                this.serviceTest.status = resp.status
                this.serviceTest.output = typeof resp.data === 'string' ? resp.data : JSON.stringify(resp.data, null, 2)
            } catch (err) {
                this.serviceTest.time = Date.now() - start
                this.serviceTest.status = err.response?.status || 500
                this.serviceTest.output = JSON.stringify(err.response?.data || { error: err.message }, null, 2)
            } finally {
                this.serviceTest.loading = false
            }
        },
        serviceUrl(svc) {
            const apiHost = this.$env.get('API_HOST') || 'http://localhost:8085'
            const template = this.$env.get('FORGE_SERVICE_ENDPOINT') || `${apiHost}/luma/forge-runtime/api/services/{appUuid}/{serviceName}`
            return template.replace('{appUuid}', this.app.uuid).replace('{serviceName}', svc.name)
        },
        save() {
            let popup = this.$loadingPopup()
            this.$http.patch(`/luma/appservice/v1/forge/${this.uuid}/settings`, this.app).then(() => {
                this.isDirty = false
                this.$bvToast.toast(this.$t('settings.settingsSaved'), {
                    variant: 'success',
                    title: this.$t('success'),
                    autoHideDelay: 3000
                })
            }).catch(err => {
                this.$debugLog(err)
                const errorMessage = err.response && err.response.data && err.response.data.message
                    ? err.response.data.message
                    : this.$t('settings.failedToSaveSettings')
                this.$bvToast.toast(errorMessage, {
                    variant: 'danger',
                    title: this.$t('error')
                })
                this.loadSettings()
            }).finally(() => popup.hide())
        },

        reinstall() {
            this.permDialog = {
                show: true,
                title: this.$t('marketplace.reinstall'),
                leadMessage: this.$t('marketplace.confirmReinstall'),
                version: this.previewVersion,
                action: 'reinstall'
            }
        },

        async applyUpdate() {
            const confirmed = await this.$mainDialog.msgBoxConfirm(
                this.$t('marketplace.confirmUpdate'),
                {
                    title: this.$t('marketplace.update'),
                    okVariant: 'warning',
                    okTitle: this.$t('marketplace.update'),
                    cancelTitle: this.$t('cancel'),
                    centered: true
                }
            )
            if (!confirmed) return

            let popup = this.$loadingPopup(this.$t('marketplace.updatingApp'))
            try {
                const env = this.$store.getters.getCurrentEnv
                await this.$http.post(
                    `/luma/appservice/v1/forge/${this.uuid}/apply-update`,
                    {},
                    { headers: { 'X-Project-Env': env } }
                )
                await this.$store.dispatch('fetchAllForgeApps')
                this.$store.commit('RELOAD_CONTAINER')
                this.$bvToast.toast(this.$t('marketplace.appUpdateInitiated'), {
                    variant: 'success',
                    title: this.$t('success'),
                    autoHideDelay: 4000
                })
                this.loadSettings()
            } catch (err) {
                this.$debugLog(err)
                const errorMessage = err.response && err.response.data && err.response.data.message
                    ? err.response.data.message
                    : this.$t('marketplace.failedToUpdate')
                this.$bvToast.toast(errorMessage, {
                    variant: 'danger',
                    title: this.$t('error')
                })
            } finally {
                popup.hide()
            }
        },

        async remove() {
            let confirm = await this.$mainDialog.msgBoxConfirm(this.$t('areYouSure'))
            if (!confirm) return

            let popup = this.$loadingPopup()
            this.$http.delete("/luma/appservice/v1/forge/" + this.uuid).then(() => {
                this.$router.push("/admin/forge")
            }).catch(err => {
                this.$debugLog(err)
                this.$mainDialog.msgBoxOk("Failed to remove app.")
            }).finally(() => popup.hide())
        },

        saveAppSettings(config) {
            set(this.app, `config.env.${this.currentEnv}`, config)
            this.save()
        },

        forkVersion() {
            const ver = cloneDeep(this.app.settings.find(s => {
                return s.version == this.previewVersion
            }))
            ver.version = this.forkTo
            this.app.settings.push(ver)
            this.save()
        },

        removeVersion() {
            this.$mainDialog.msgBoxConfirm("You are about to remove version " + this.ideVersion +". Are you sure?").then(resp => {
                if (resp) {
                    let popup = this.$loadingPopup()
                    this.$http.delete(`/luma/appservice/v1/forge/${this.uuid}/version/${this.ideVersion}`).then(() => {
                        this.app.settings = this.app.settings.filter(s => {
                            return s.version != this.ideVersion
                        })
                        if (this.previewVersion == this.ideVersion) {
                            this.previewVersion = this.app.settings[0].version
                        }
                    })
                    .catch(err => {
                        this.$debugLog(err)
                        this.$bvToast.toast("Failed to save app settings.", {
                            variant: 'danger'
                        })
                    }).finally(() => popup.hide())
                }
            })
        },

        publishDevVersion(version) {
            if (version == '') {
                this.$mainDialog.msgBoxConfirm(
                    "You are about to deactivate app from dev environment, this action will also unpublish app if it is live. Are you sure?"
                ).then(resp => {
                    if (!resp) return
                    const originalVersion = this.app.dev_version
                    this.app.dev_version = version

                    let popup = this.$loadingPopup()
                    this.$http.patch(`/luma/appservice/v1/forge/${this.uuid}/settings`, this.app).then(() => {
                        this.$bvToast.toast(this.$t('settings.settingsSaved'), {
                            variant: 'success',
                            title: this.$t('success'),
                            autoHideDelay: 3000
                        })
                    }).catch(err => {
                        this.$debugLog(err)
                        const errorMessage = err.response && err.response.data && err.response.data.message
                            ? err.response.data.message
                            : this.$t('settings.failedToSaveSettings')
                        this.$bvToast.toast(errorMessage, {
                            variant: 'danger',
                            title: this.$t('error')
                        })
                        this.app.dev_version = originalVersion
                    }).finally(() => popup.hide())
                })
                return
            }

            this.permDialog = {
                show: true,
                title: this.$t('deployVersionTitle'),
                leadMessage: this.$t('confirmDeployToEnv', { version: version, env: this.$t('developmentEnv') }),
                version: version,
                action: 'deploy-dev'
            }
        },

        publishLiveVersion(version) {
            if (version == '') {
                this.$mainDialog.msgBoxConfirm(
                    "You are about to unpublish app from live environment. Are you sure?"
                ).then(resp => {
                    if (!resp) return
                    const originalVersion = this.app.public_version
                    this.app.public_version = version

                    let popup = this.$loadingPopup()
                    this.$http.patch(`/luma/appservice/v1/forge/${this.uuid}/settings`, this.app).then(() => {
                        this.$bvToast.toast(this.$t('settings.settingsSaved'), {
                            variant: 'success',
                            title: this.$t('success'),
                            autoHideDelay: 3000
                        })
                    }).catch(err => {
                        this.$debugLog(err)
                        const errorMessage = err.response && err.response.data && err.response.data.message
                            ? err.response.data.message
                            : this.$t('settings.failedToSaveSettings')
                        this.$bvToast.toast(errorMessage, {
                            variant: 'danger',
                            title: this.$t('error')
                        })
                        this.app.public_version = originalVersion
                    }).finally(() => popup.hide())
                })
                return
            }

            this.permDialog = {
                show: true,
                title: this.$t('deployVersionTitle'),
                leadMessage: this.$t('confirmDeployToEnv', { version: version, env: this.$t('liveEnv') }),
                version: version,
                action: 'deploy-live'
            }
        },

        async previewForge() {
            let url = this.$env.get("FORGE_APP_URL_PATTERN").replaceAll("{name}", this.app.name)
            url = `${url}/launch?env=${this.$store.getters.getCurrentEnv}&lang=${this.$store.getters.getLocale}&name=${this.app.name}`
            if (url.startsWith("http://localhost")) {
                url = `${url}&name=${this.app.name}`
            }
            openForgeAppWithToken(this, url, this.app.name)
        },

        executePermDialogAction() {
            var action = this.permDialog.action
            var version = this.permDialog.version
            this.permDialog.show = false

            if (action === 'reinstall') {
                var popup = this.$loadingPopup(this.$t('marketplace.reinstallingApp'))
                this.$http.patch('/luma/appservice/v1/forge/' + this.uuid + '/settings?reinstall=' + this.previewVersion, {}).then(() => {
                    this.$store.dispatch('fetchAllForgeApps')
                    this.$store.commit('RELOAD_CONTAINER')
                    this.$bvToast.toast(this.$t('marketplace.appReinstalled'), {
                        variant: 'success',
                        title: this.$t('success'),
                        autoHideDelay: 3000
                    })
                }).catch(err => {
                    this.$debugLog(err)
                    var errorMessage = err.response && err.response.data && err.response.data.message
                        ? err.response.data.message
                        : this.$t('marketplace.failedToReinstallApp')
                    this.$bvToast.toast(errorMessage, {
                        variant: 'danger',
                        title: this.$t('error')
                    })
                    this.loadSettings()
                }).finally(function () { popup.hide() })
            } else if (action === 'deploy-dev') {
                var originalDev = this.app.dev_version
                this.app.dev_version = version

                var popup2 = this.$loadingPopup()
                this.$http.patch('/luma/appservice/v1/forge/' + this.uuid + '/settings', this.app).then(() => {
                    this.$bvToast.toast(this.$t('settings.settingsSaved'), {
                        variant: 'success',
                        title: this.$t('success'),
                        autoHideDelay: 3000
                    })
                }).catch(err => {
                    this.$debugLog(err)
                    var errorMessage = err.response && err.response.data && err.response.data.message
                        ? err.response.data.message
                        : this.$t('settings.failedToSaveSettings')
                    this.$bvToast.toast(errorMessage, {
                        variant: 'danger',
                        title: this.$t('error')
                    })
                    this.app.dev_version = originalDev
                }).finally(function () { popup2.hide() })
            } else if (action === 'deploy-live') {
                var originalLive = this.app.public_version
                this.app.public_version = version

                var popup3 = this.$loadingPopup()
                this.$http.patch('/luma/appservice/v1/forge/' + this.uuid + '/settings', this.app).then(() => {
                    this.$bvToast.toast(this.$t('settings.settingsSaved'), {
                        variant: 'success',
                        title: this.$t('success'),
                        autoHideDelay: 3000
                    })
                }).catch(err => {
                    this.$debugLog(err)
                    var errorMessage = err.response && err.response.data && err.response.data.message
                        ? err.response.data.message
                        : this.$t('settings.failedToSaveSettings')
                    this.$bvToast.toast(errorMessage, {
                        variant: 'danger',
                        title: this.$t('error')
                    })
                    this.app.public_version = originalLive
                }).finally(function () { popup3.hide() })
            }
        },

        doCleanupApp() {
            this.cleanupDialog.show = true
        },

        executeCleanupApp() {
            this.cleaningUp = true
            this.cleanupDialog.show = false
            var popup = this.$loadingPopup(this.$t('forgeCleaningUp'))
            this.$http.post('/luma/appservice/v1/forge/' + this.uuid + '/cleanup')
                .then(() => {
                    this.loadSettings()
                    this.$bvToast.toast(this.$t('forgeCleanupDone'), {
                        variant: 'success',
                        title: this.$t('success')
                    })
                })
                .catch(() => {
                    this.$bvToast.toast(this.$t('forgeCleanupFailed'), {
                        variant: 'danger',
                        title: this.$t('error')
                    })
                })
                .finally(() => {
                    this.cleaningUp = false
                    popup.hide()
                })
        },

        addVariable(env) {
            const key = this.newVar[env].key.trim()
            if (!key) return
            if (!this.app.variables) this.$set(this.app, 'variables', { dev: {}, live: {} })
            if (!this.app.variables[env]) this.$set(this.app.variables, env, {})
            this.$set(this.app.variables[env], key, this.newVar[env].value)
            this.newVar[env].key = ''
            this.newVar[env].value = ''
        },

        removeVariable(env, key) {
            if (!this.app.variables || !this.app.variables[env]) return
            this.$delete(this.app.variables[env], key)
        },

        updateVariable(env, key, value) {
            if (!this.app.variables || !this.app.variables[env]) return
            this.$set(this.app.variables[env], key, value)
        },

        loadSettings() {
            return this.$http.get(`/luma/appservice/v1/forge/${this.uuid}/settings`).then(resp => {
                this.app = resp.data
                if (!this.app.variables) {
                    this.$set(this.app, 'variables', { dev: {}, live: {} })
                } else {
                    if (!this.app.variables.dev) this.$set(this.app.variables, 'dev', {})
                    if (!this.app.variables.live) this.$set(this.app.variables, 'live', {})
                }

                const mergeUpdateAvailable = () => {
                    const storeApps = this.$store.getters.getForgeApps || []
                    const storeApp = storeApps.find(a => a.uuid === this.uuid)
                    if (storeApp && storeApp.update_available) {
                        this.$set(this.app, 'update_available', storeApp.update_available)
                    }
                }
                if (this.$store.getters.getForgeApps && this.$store.getters.getForgeApps.length) {
                    mergeUpdateAvailable()
                } else {
                    this.$store.dispatch('fetchForgeAppsForWorkspace').then(mergeUpdateAvailable).catch(() => {})
                }

                this.previewVersion = this.app.dev_version
                this.localGrants = (this.app.grants && this.app.grants.slice()) || []

                const apiUser = get(this.app, 'config.api_user')
                if (apiUser) {
                    this.loadApiUserRoles(apiUser)
                }

                this.$nextTick(() => {
                    this.modelLoaded = true
                    this.isDirty = false
                })
            }).catch(err => {
                this.$debugLog(err)
                this.$mainDialog.msgBoxOk("Failed to fetch app.")
            })
        },

        async loadApiUserRoles(apiUserUuid) {
            try {
                const resp = await this.$http.get(`/v1/user/api-user/${apiUserUuid}`)
                this.apiUserRoles = (resp.data.roles || []).map(r => r.role).filter(r => r)
            } catch (err) {
                this.$debugLog(err)
                this.apiUserRoles = []
            }
        },

        grantsIsForced(grant) {
            return grant.indexOf('!') === 0
        },

        grantsDisplayName(grant) {
            return grant.indexOf('!') === 0 ? grant.substring(1) : grant
        },

        grantsToggleForcedInList(grant) {
            var index = this.localGrants.indexOf(grant)
            if (index === -1) return
            var toggled = grant.indexOf('!') === 0 ? grant.substring(1) : '!' + grant
            this.$set(this.localGrants, index, toggled)
        },

        grantsDraftToggleForcedInList(grant) {
            var index = this.grantsDraft.indexOf(grant)
            if (index === -1) return
            var toggled = grant.indexOf('!') === 0 ? grant.substring(1) : '!' + grant
            this.$set(this.grantsDraft, index, toggled)
        },

        grantsIsPermSelected(perm, component) {
            var full = perm + ' ' + component
            return this.localGrants.indexOf(full) !== -1 || this.localGrants.indexOf('!' + full) !== -1
        },

        grantsDraftIsPermSelected(perm, component) {
            var full = perm + ' ' + component
            return this.grantsDraft.indexOf(full) !== -1 || this.grantsDraft.indexOf('!' + full) !== -1
        },

        grantsDraftAreAllSelected(group, component, permList) {
            for (var i = 0; i < permList.length; i++) {
                if (!this.grantsDraftIsPermSelected(permList[i], component)) return false
            }
            return permList.length > 0
        },

        grantsDraftIsSomeSelected(group, component, permList) {
            var count = 0
            for (var i = 0; i < permList.length; i++) {
                if (this.grantsDraftIsPermSelected(permList[i], component)) count++
            }
            return count > 0 && count < permList.length
        },

        grantsDraftHasSelectedPerms(group, component) {
            var groupVal = this.grantsPermissionsList[group] || {}
            var perms = groupVal[component] || []
            for (var i = 0; i < perms.length; i++) {
                if (this.grantsDraftIsPermSelected(perms[i], component)) return true
            }
            return false
        },

        grantsDraftGetSelectedCount(group, component, permList) {
            if (!permList) return 0
            var count = 0
            for (var i = 0; i < permList.length; i++) {
                if (this.grantsDraftIsPermSelected(permList[i], component)) count++
            }
            return count
        },

        grantsDraftTogglePerm(perm, component) {
            var fullPerm = perm + ' ' + component
            var index = this.grantsDraft.indexOf(fullPerm)
            var forcedIndex = this.grantsDraft.indexOf('!' + fullPerm)
            if (index === -1 && forcedIndex === -1) {
                this.grantsDraft.push(fullPerm)
            } else if (index !== -1) {
                this.grantsDraft.splice(index, 1)
            } else {
                this.grantsDraft.splice(forcedIndex, 1)
            }
        },

        grantsDraftToggleAllComponentPerms(group, component, permList) {
            var allSelected = this.grantsDraftAreAllSelected(group, component, permList)
            for (var i = 0; i < permList.length; i++) {
                var fullPerm = permList[i] + ' ' + component
                var index = this.grantsDraft.indexOf(fullPerm)
                var forcedIndex = this.grantsDraft.indexOf('!' + fullPerm)
                if (allSelected) {
                    if (index !== -1) this.grantsDraft.splice(index, 1)
                    else if (forcedIndex !== -1) this.grantsDraft.splice(forcedIndex, 1)
                } else if (index === -1 && forcedIndex === -1) {
                    this.grantsDraft.push(fullPerm)
                }
            }
        },

        grantsDraftRemoveFromList(fullPerm) {
            var index = this.grantsDraft.indexOf(fullPerm)
            if (index !== -1) this.grantsDraft.splice(index, 1)
        },

        grantsAreAllSelected(group, component, permList) {
            for (var i = 0; i < permList.length; i++) {
                if (!this.grantsIsPermSelected(permList[i], component)) return false
            }
            return permList.length > 0
        },

        grantsIsSomeSelected(group, component, permList) {
            var count = 0
            for (var i = 0; i < permList.length; i++) {
                if (this.grantsIsPermSelected(permList[i], component)) count++
            }
            return count > 0 && count < permList.length
        },

        grantsHasSelectedPerms(group, component) {
            var groupVal = this.grantsPermissionsList[group] || {}
            var perms = groupVal[component] || []
            for (var i = 0; i < perms.length; i++) {
                if (this.grantsIsPermSelected(perms[i], component)) return true
            }
            return false
        },

        grantsGetSelectedCount(group, component, permList) {
            var count = 0
            for (var i = 0; i < permList.length; i++) {
                if (this.grantsIsPermSelected(permList[i], component)) count++
            }
            return count
        },

        grantsGetGroupGrantCount(group) {
            var groupVal = this.grantsPermissionsList[group] || {}
            var total = 0
            var components = Object.keys(groupVal)
            for (var i = 0; i < components.length; i++) {
                total += groupVal[components[i]].length
            }
            return total
        },

        grantsTogglePerm(perm, component) {
            var fullPerm = perm + ' ' + component
            var index = this.localGrants.indexOf(fullPerm)
            var forcedIndex = this.localGrants.indexOf('!' + fullPerm)
            if (index === -1 && forcedIndex === -1) {
                this.localGrants.push(fullPerm)
            } else if (index !== -1) {
                this.localGrants.splice(index, 1)
            } else {
                this.localGrants.splice(forcedIndex, 1)
            }
        },

        grantsToggleAllComponentPerms(group, component, permList) {
            var allSelected = this.grantsAreAllSelected(group, component, permList)
            for (var i = 0; i < permList.length; i++) {
                var fullPerm = permList[i] + ' ' + component
                var index = this.localGrants.indexOf(fullPerm)
                var forcedIndex = this.localGrants.indexOf('!' + fullPerm)
                if (allSelected) {
                    if (index !== -1) this.localGrants.splice(index, 1)
                    else if (forcedIndex !== -1) this.localGrants.splice(forcedIndex, 1)
                } else if (index === -1 && forcedIndex === -1) {
                    this.localGrants.push(fullPerm)
                }
            }
        },

        grantsRemoveFromList(fullPerm) {
            var index = this.localGrants.indexOf(fullPerm)
            if (index !== -1) this.localGrants.splice(index, 1)
        },

        grantsClearAll() {
            this.localGrants = []
        },

        openGrantsDialog() {
            this.grantsDraft = this.localGrants.slice()
            this.grantsFilter = ''
            this.grantsDialogShow = true
            var self = this
            this.$nextTick(function() {
                self.grantsCheckTabsOverflow()
            })
        },

        grantsDialogApply() {
            this.localGrants = this.grantsDraft.slice()
            this.grantsDialogShow = false
        },

        grantsFormatGroupName(group) {
            return group.replace(/_/g, ' ').replace(/\b\w/g, function(c) { return c.toUpperCase() })
        },

        grantsFormatComponentName(val) {
            return val.replace(/thunder/g, '').replace(/::/g, ' ').replace(/_/g, ' ').replace(/\b\w/g, function(c) { return c.toUpperCase() }).trim()
        },

        grantsFormatPermissionName(perm) {
            return perm.replace('function::', '').replace(/::/g, ' ').replace(/_/g, ' ')
        },

        grantsScrollTabs(direction) {
            var wrapper = this.$refs.grantsTabsWrapper
            if (!wrapper) return
            var scrollAmount = 200
            wrapper.scrollTo({ left: wrapper.scrollLeft + (direction === 'left' ? -scrollAmount : scrollAmount), behavior: 'smooth' })
        },

        grantsHandleTabScroll() {
            var wrapper = this.$refs.grantsTabsWrapper
            if (!wrapper) return
            this.grantsShowLeftArrow = wrapper.scrollLeft > 0
            this.grantsShowRightArrow = wrapper.scrollLeft < (wrapper.scrollWidth - wrapper.clientWidth - 1)
        },

        grantsCheckTabsOverflow() {
            var self = this
            this.$nextTick(function() {
                var wrapper = self.$refs.grantsTabsWrapper
                if (!wrapper) return
                self.grantsShowRightArrow = wrapper.scrollWidth > wrapper.clientWidth
                self.grantsHandleTabScroll()
            })
        },

        async saveGrants() {
            this.savingGrants = true
            try {
                await this.$http.patch(`/luma/appservice/v1/forge/${this.uuid}/grants`, { grants: this.localGrants })
                this.$bvToast.toast(this.$t('marketplace.installWizard.grantsSaved'), { variant: 'success', title: this.$t('success') })
            } catch (err) {
                this.$debugLog(err)
                this.$bvToast.toast((err.response && err.response.data && err.response.data.message) || this.$t('error'), { variant: 'danger', title: this.$t('error') })
            } finally {
                this.savingGrants = false
            }
        },

        isDomainVerified(domain) {
            return !domain.startsWith('!')
        },

        getDomainName(domain) {
            return domain.startsWith('!') ? domain.substring(1) : domain
        },

        async addDomain() {
            if (!this.newDomain.trim()) return
            const domain = this.newDomain.trim().toLowerCase()

            if (this.customDomains.some(d => this.getDomainName(d) === domain)) {
                this.$bvToast.toast(this.$t('domainAlreadyExists'), {
                    variant: 'warning',
                    title: this.$t('warning')
                })
                return
            }

            const popup = this.$loadingPopup()
            try {
                const resp = await this.$http.post(`/luma/appservice/v1/forge/${this.uuid}/domain`, { domain })
                this.customDomains.push(`!${domain}`)
                this.$set(this.domainValidationKeys, domain, resp.data.validationKey)
                this.newDomain = ''
                this.$bvToast.toast(this.$t('domainAddedSuccessfully'), {
                    variant: 'success',
                    title: this.$t('success')
                })
            } catch (err) {
                this.$debugLog(err)
                const errorMessage = err.response && err.response.data && err.response.data.message
                    ? err.response.data.message
                    : this.$t('failedToAddDomain')
                this.$bvToast.toast(errorMessage, {
                    variant: 'danger',
                    title: this.$t('error')
                })
            } finally {
                popup.hide()
            }
        },

        removeDomain(index) {
            this.$mainDialog.msgBoxConfirm(this.$t('confirmRemoveDomain')).then(async confirmed => {
                if (!confirmed) return
                const domain = this.getDomainName(this.customDomains[index])
                const popup = this.$loadingPopup(this.$t('removingDomain'))
                try {
                    await this.$http.delete(`/luma/appservice/v1/forge/${this.uuid}/domain`, {
                        data: { domain }
                    })
                    this.customDomains.splice(index, 1)
                    this.$delete(this.domainValidationKeys, domain)
                    this.$bvToast.toast(this.$t('domainRemovedSuccessfully'), {
                        variant: 'success',
                        title: this.$t('success')
                    })
                } catch (err) {
                    this.$debugLog(err)
                    const errorMessage = err.response && err.response.data && err.response.data.message
                        ? err.response.data.message
                        : this.$t('failedToRemoveDomain')
                    this.$bvToast.toast(errorMessage, {
                        variant: 'danger',
                        title: this.$t('error')
                    })
                } finally {
                    popup.hide()
                }
            })
        },

        async verifyDomain(domain) {
            const domainName = this.getDomainName(domain)
            const popup = this.$loadingPopup(this.$t('verifyingDomain'))
            try {
                await this.$http.post(`/luma/appservice/v1/forge/${this.uuid}/domain/verify`, { domain: domainName })
                const index = this.customDomains.indexOf(domain)
                if (index !== -1) {
                    this.$set(this.customDomains, index, domainName)
                    this.$delete(this.domainValidationKeys, domainName)
                }
                this.$bvToast.toast(this.$t('domainVerifiedSuccessfully'), {
                    variant: 'success',
                    title: this.$t('success')
                })
            } catch (err) {
                this.$debugLog(err)
                const errorMessage = err.response && err.response.data && err.response.data.message
                    ? err.response.data.message
                    : this.$t('domainVerificationFailed')
                this.$bvToast.toast(errorMessage, {
                    variant: 'danger',
                    title: this.$t('error')
                })
            } finally {
                popup.hide()
            }
        },

        copyToClipboard(text) {
            navigator.clipboard.writeText(text).then(() => {
                this.$bvToast.toast(this.$t('copiedToClipboard'), {
                    variant: 'success',
                    autoHideDelay: 2000
                })
            })
        },

        async refreshValidationKey(domain) {
            const domainName = this.getDomainName(domain)
            const popup = this.$loadingPopup(this.$t('refreshingValidationKey'))
            try {
                const resp = await this.$http.post(`/luma/appservice/v1/forge/${this.uuid}/domain/refresh`, { domain: domainName })
                this.$set(this.domainValidationKeys, domainName, resp.data.validationKey)
                this.$bvToast.toast(this.$t('validationKeyRefreshed'), {
                    variant: 'success',
                    title: this.$t('success')
                })
            } catch (err) {
                this.$debugLog(err)
                const errorMessage = err.response && err.response.data && err.response.data.message
                    ? err.response.data.message
                    : this.$t('failedToRefreshValidationKey')
                this.$bvToast.toast(errorMessage, {
                    variant: 'danger',
                    title: this.$t('error')
                })
            } finally {
                popup.hide()
            }
        },

        manageApiUser() {
            const apiUser = get(this.app, 'config.api_user')
            if (!apiUser) return
            this.$router.push(`/admin/iam/apiusers/edit/${apiUser}`)
        },

        openRoleEditModal(role) {
            console.log("Editing role:", role)
            this.roleEditModal.roleId = role.uuid
            this.roleEditModal.show = true
        },

        onRoleUpdated() {
            this.roleEditModal.show = false
            const apiUser = get(this.app, 'config.api_user')
            if (apiUser) {
                this.loadApiUserRoles(apiUser)
            }
        },

        resetRoleEditModal() {
            this.roleEditModal.roleId = null
        },

        async refreshApiUserToken() {
            const apiUser = get(this.app, 'config.api_user')
            if (!apiUser) return

            this.$mainDialog.msgBoxConfirm(this.$t('confirmRefreshToken'), {
                title: this.$t('refreshToken'),
                okVariant: 'warning',
                okTitle: this.$t('refresh'),
                cancelTitle: this.$t('cancel')
            }).then(async confirmed => {
                if (!confirmed) return
                const popup = this.$loadingPopup(this.$t('refreshingToken'))
                try {
                    await this.$http.post(`/luma/appservice/v1/forge/${this.uuid}/token/refresh`)
                    this.$bvToast.toast(this.$t('tokenRefreshedSuccessfully'), {
                        variant: 'success',
                        title: this.$t('success')
                    })
                } catch (err) {
                    this.$debugLog(err)
                    const errorMessage = err.response && err.response.data && err.response.data.message
                        ? err.response.data.message
                        : this.$t('failedToRefreshToken')
                    this.$bvToast.toast(errorMessage, {
                        variant: 'danger',
                        title: this.$t('error')
                    })
                } finally {
                    popup.hide()
                }
            })
        },

        handleKeydown(e) {
            if ((e.metaKey || e.ctrlKey) && e.key === 's') {
                e.preventDefault()
                this.save()
            }
        }
    },
    mounted() {
        this.loadSettings()
        var self = this
        this.$store.dispatch('fetchPermissionsList').then(function() {
            self.grantsCheckTabsOverflow()
        })

        document.addEventListener('keydown', this.handleKeydown)

        // ── Kortex AI tools ──────────────────────────────────────
        if (!window.kortex) return

        window.kortex.showHelper()
        window.kortex.registerContext(
            'Forge App Editor',
            'BEFORE YOU DO ANYTHING ELSE: first search the internal knowledge base for relevant topics. ' +
            'The user is editing a Forge application. You can read the app configuration, save changes, ' +
            'and manage dev/live versions. Use get_forge_info to see the current state first.',
            'fa-cubes'
        )

        window.kortex.registerTool(
            {
                type: 'function',
                function: {
                    name: 'get_forge_info',
                    description: 'Returns the forge application metadata: name, UUID, status, dev/live versions, available versions, hostname, and grants.',
                    parameters: { type: 'object', properties: {} }
                }
            },
            function () {
                var app = self.app
                if (!app) return { error: 'Forge app not loaded yet' }
                return {
                    uuid: app.uuid,
                    name: app.name,
                    type: app.type || null,
                    icon: app.icon || null,
                    status: app.status || null,
                    dev_version: app.dev_version || null,
                    live_version: app.public_version || null,
                    available_versions: (app.settings || []).map(function (s) { return s.version }),
                    update_available: app.update_available || false,
                    hostname: self.hostname || null,
                    grants: self.localGrants || [],
                    services: self.activeServices || [],
                    api_user: app.config && app.config.api_user ? app.config.api_user : null
                }
            }
        )

        window.kortex.registerTool(
            {
                type: 'function',
                function: {
                    name: 'save_forge',
                    description: 'Persists the current forge app settings to the server.',
                    parameters: { type: 'object', properties: {} }
                }
            },
            function () {
                self.save()
                return { saved: true, note: 'Save request sent.' }
            }
        )

        window.kortex.registerTool(
            {
                type: 'function',
                function: {
                    name: 'publish_forge_version',
                    description: 'Sets the dev or live version of the forge app to the specified version tag.',
                    parameters: {
                        type: 'object',
                        required: ['version', 'type'],
                        properties: {
                            version: { type: 'string', description: 'The version tag to publish.' },
                            type: { type: 'string', enum: ['dev', 'live'], description: 'Which environment: "dev" or "live".' }
                        }
                    }
                }
            },
            function (args) {
                var version = args.version
                var type = args.type
                if (type !== 'dev' && type !== 'live') return { error: 'type must be "dev" or "live".' }
                var available = (self.app.settings || []).map(function (s) { return s.version })
                if (available.indexOf(version) === -1) {
                    return { error: 'Version "' + version + '" not found. Available: ' + available.join(', ') }
                }
                if (type === 'dev') {
                    self.$set(self.app, 'dev_version', version)
                } else {
                    self.$set(self.app, 'public_version', version)
                }
                self.save()
                return { published: true, version: version, type: type }
            }
        )
    },

    beforeDestroy() {
        document.removeEventListener('keydown', this.handleKeydown)

        if (!window.kortex) return
        window.kortex.unregisterContext()
        window.kortex.hideHelper()
        window.kortex.unregisterTool('get_forge_info')
        window.kortex.unregisterTool('save_forge')
        window.kortex.unregisterTool('publish_forge_version')
    },

    watch: {
        app: {
            deep: true,
            handler() {
                if (this.modelLoaded) this.isDirty = true
            }
        }
    }
}
</script>

<style scoped>
/* ── Layout ─────────────────────────────────────── */
.fg-edit {
    display: flex;
    flex-direction: column;
    min-height: calc(100vh - 120px);
}

/* ── Header ─────────────────────────────────────── */
.fg-header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 10px 0;
    border-bottom: 1px solid var(--border-default);
}

.fg-header-left {
    display: flex;
    align-items: center;
    gap: 10px;
    min-width: 0;
}

.fg-header-right {
    display: flex;
    align-items: center;
    gap: 8px;
    flex-shrink: 0;
}

.fg-back {
    display: flex;
    align-items: center;
    justify-content: center;
    width: 32px;
    height: 32px;
    border-radius: var(--radius-sm, 6px);
    color: var(--text-secondary);
    transition: all 0.15s;
}
.fg-back:hover {
    background: var(--bg-card-secondary);
    color: var(--text-primary);
    text-decoration: none;
}

.fg-title {
    font-size: 18px;
    font-weight: 600;
    color: var(--text-primary);
    margin: 0;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
}

.fg-status-badge {
    font-size: 10px;
    font-weight: 500;
    letter-spacing: 0.3px;
    flex-shrink: 0;
}

.fg-unsaved {
    width: 8px;
    height: 8px;
    border-radius: 50%;
    background: var(--warning-start, #ed8936);
    display: inline-block;
    animation: fg-pulse 2s infinite;
    flex-shrink: 0;
}

@keyframes fg-pulse {
    0%, 100% { opacity: 1; }
    50% { opacity: 0.3; }
}

.fg-save-btn {
    font-weight: 500;
}

.fg-kbd {
    display: inline-block;
    padding: 1px 4px;
    font-size: 10px;
    font-family: inherit;
    background: rgba(255, 255, 255, 0.15);
    border-radius: 3px;
    opacity: 0.7;
    line-height: 1.4;
}

/* ── Version Pipeline ───────────────────────────── */
.fg-versions {
    display: flex;
    align-items: center;
    gap: 6px;
    padding: 10px 0;
    border-bottom: 1px solid var(--border-default);
    overflow-x: auto;
    scrollbar-width: none;
}
.fg-versions::-webkit-scrollbar {
    display: none;
}

.fg-version-stage {
    display: flex;
    align-items: center;
    gap: 6px;
    flex-shrink: 0;
}

.fg-version-label {
    font-size: 10px;
    font-weight: 700;
    text-transform: uppercase;
    letter-spacing: 0.6px;
    color: var(--text-muted, #949ba4);
    white-space: nowrap;
}

.fg-version-select {
    width: auto;
    min-width: 72px;
    max-width: 110px;
    font-size: 12px;
    padding: 2px 24px 2px 8px;
    height: 28px;
}

.fg-version-action {
    padding: 2px 4px;
    color: var(--text-secondary);
    line-height: 1;
}

.fg-version-promote {
    display: flex;
    align-items: center;
    flex-shrink: 0;
}

.fg-version-arrow {
    color: var(--text-muted, #949ba4);
    font-size: 11px;
    opacity: 0.6;
}

/* ── Version Chips ──────────────────────────────── */
.fg-version-chip {
    display: inline-flex;
    align-items: center;
    gap: 6px;
    padding: 5px 12px;
    border-radius: var(--radius-sm, 6px);
    font-size: 13px;
    font-weight: 600;
    white-space: nowrap;
    line-height: 1.3;
}

.fg-version-chip-icon {
    font-size: 14px;
}

.fg-version-chip--success {
    background: rgba(72, 187, 120, 0.12);
    color: var(--success-start, #48bb78);
    border: 1px solid rgba(72, 187, 120, 0.25);
}

.fg-version-chip--live {
    background: rgba(108, 128, 255, 0.12);
    color: var(--primary-start, #6c80ff);
    border: 1px solid rgba(108, 128, 255, 0.25);
}

.fg-version-chip--inactive {
    background: var(--bg-card-secondary, rgba(255, 255, 255, 0.05));
    color: var(--text-muted, #949ba4);
    border: 1px solid var(--border-default);
}

/* ── Error Alert ────────────────────────────────── */
.fg-error-alert {
    margin-top: 8px;
    border-radius: var(--radius-sm, 6px);
}

/* ── External App Name ──────────────────────────── */
.fg-external-name {
    padding: 12px 0;
    border-bottom: 1px solid var(--border-default);
}

/* ── Tabs ───────────────────────────────────────── */
.fg-tabs {
    flex: 1;
    display: flex;
    flex-direction: column;
}

.fg-tabs >>> .fg-tab-nav {
    border-bottom: 1px solid var(--border-default);
    padding-top: 4px;
}

.fg-tabs >>> .fg-tab-nav .nav-link {
    font-size: 13px;
    font-weight: 500;
    color: var(--text-secondary);
    padding: 8px 14px;
    border: none;
    border-bottom: 2px solid transparent;
    transition: all 0.15s;
}

.fg-tabs >>> .fg-tab-nav .nav-link:hover {
    color: var(--text-primary);
    border-bottom-color: var(--border-hover, rgba(102, 126, 234, 0.25));
}

.fg-tabs >>> .fg-tab-nav .nav-link.active {
    color: var(--primary-start, #6c80ff);
    border-bottom-color: var(--primary-start, #6c80ff);
    background: transparent;
}

.fg-tabs >>> .fg-tab-body {
    flex: 1;
}

.fg-tab-pad {
    padding: 16px 0;
    max-width: 720px;
}

/* ── Settings rows ──────────────────────────────── */
.fg-setting {
    padding: 16px 0;
    border-bottom: 1px solid var(--border-default);
}
.fg-setting:first-child {
    padding-top: 8px;
}
.fg-setting:last-child {
    border-bottom: none;
}

.fg-setting-label {
    font-weight: 600;
    font-size: 13px;
    color: var(--text-primary);
    margin-bottom: 8px;
    display: block;
}

/* ── Empty states ───────────────────────────────── */
.fg-empty-state {
    display: flex;
    align-items: center;
    justify-content: center;
    padding: 28px;
    color: var(--text-muted);
    font-size: 13px;
    border: 1px dashed var(--border-default);
    border-radius: var(--radius-sm, 6px);
}

/* ── Domain cards ───────────────────────────────── */
.fg-domain-card {
    padding: 14px;
    border: 1px solid var(--border-default);
    border-left: 3px solid var(--border-default);
    border-radius: var(--radius-sm, 6px);
    margin-bottom: 8px;
    transition: border-color 0.2s;
}
.fg-domain-card:hover {
    border-left-color: var(--primary-start, #6c80ff);
}

.fg-domain-status {
    display: inline-flex;
    align-items: center;
    padding: 2px 8px;
    border-radius: 4px;
    font-size: 11px;
    font-weight: 600;
}
.fg-domain-status--verified {
    background: rgba(72, 187, 120, 0.12);
    color: var(--success-start, #48bb78);
}
.fg-domain-status--pending {
    background: rgba(237, 137, 54, 0.12);
    color: var(--warning-start, #ed8936);
}

.domain-list {
    max-height: 500px;
    overflow-y: auto;
}

.verification-info {
    background: var(--bg-card-secondary);
    padding: 12px;
    border-radius: 6px;
    margin-top: 8px;
}

/* ── API User ───────────────────────────────────── */
.fg-api-user-card {
    background: var(--bg-card-secondary);
    border: 1px solid var(--border-default);
    border-radius: var(--radius-sm, 6px);
    padding: 16px;
    transition: border-color 0.2s;
}
.fg-api-user-card:hover {
    border-color: var(--border-hover);
}

.role-badges {
    display: flex;
    flex-direction: column;
    gap: 8px;
    margin-top: 8px;
}

.role-badge-wrapper {
    display: flex;
    align-items: center;
}

.role-badge {
    font-size: 13px;
    padding: 6px 12px;
}

/* ── Permission dialog ──────────────────────────── */
.iw-section {
    margin-bottom: 1.25rem;
}
.iw-section:last-child { margin-bottom: 0; }

.iw-section-title {
    font-size: 0.78rem;
    font-weight: 600;
    text-transform: uppercase;
    letter-spacing: 0.06em;
    color: var(--text-secondary);
    margin-bottom: 0.65rem;
}

.iw-perm-row {
    display: flex;
    align-items: flex-start;
    gap: 0.65rem;
    padding: 0.5rem 0.75rem;
    border-radius: 8px;
    margin-bottom: 0.35rem;
    background: var(--form-bg);
    border: 1px solid var(--border-color, rgba(0,0,0,0.1));
}
.iw-perm-icon { font-size: 0.85rem; margin-top: 3px; flex-shrink: 0; }
.iw-perm-name { font-size: 0.82rem; color: var(--text-primary); }

.iw-section-title--entitlement { color: #b45309; }
.iw-perm-row--entitlement {
    background: rgba(245, 158, 11, 0.08);
    border-color: rgba(245, 158, 11, 0.3);
}

/* ── Grants ─────────────────────────────────────── */
.grants-tag-container {
    display: flex;
    flex-wrap: wrap;
    min-height: 2rem;
}
.grant-tag {
    display: inline-flex;
    align-items: center;
    font-size: 0.8rem;
}

.grants-tabs-container {
    position: relative;
    display: flex;
    align-items: center;
    gap: 8px;
}
.grants-tabs-wrapper {
    flex: 1;
    overflow-x: auto;
    overflow-y: hidden;
    scrollbar-width: none;
    -ms-overflow-style: none;
}
.grants-tabs-wrapper::-webkit-scrollbar { display: none; }

.grants-scroll-btn {
    flex-shrink: 0;
    width: 32px;
    height: 32px;
    border: 1px solid var(--border-default);
    background: var(--bg-card-secondary);
    border-radius: 6px;
    display: flex;
    align-items: center;
    justify-content: center;
    cursor: pointer;
    color: var(--text-secondary);
}
.grants-scroll-btn:hover {
    border-color: var(--primary-start);
    color: var(--primary-start);
}

.grants-components-grid {
    display: grid;
    grid-template-columns: repeat(auto-fill, minmax(280px, 1fr));
    gap: 16px;
}
.grants-component-card {
    background: var(--bg-card-secondary);
    border: 1px solid var(--border-default);
    border-radius: 10px;
    overflow: hidden;
    transition: border-color 0.2s;
}
.grants-component-card.has-selected {
    border-color: #d97706;
    box-shadow: 0 0 0 1px rgba(217,119,6,0.3);
}
.grants-component-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: 12px 14px;
    background: var(--bg-card);
    border-bottom: 1px solid var(--border-default);
    cursor: pointer;
}
.grants-component-header:hover { background: var(--bg-card-secondary); }
.grants-component-info .grants-component-name {
    margin: 0;
    font-size: 13px;
    font-weight: 600;
}
.grants-component-info .grants-component-count {
    font-size: 11px;
    color: var(--text-muted);
}
.grants-permissions-list { padding: 10px; }
.grants-permission-item {
    padding: 6px 10px;
    border-radius: 5px;
    cursor: pointer;
    transition: background 0.15s;
}
.grants-permission-item:hover { background: var(--bg-card); }

.grants-selected-summary {
    margin-top: 16px;
    padding: 16px;
    background: var(--bg-card-secondary);
    border-radius: 10px;
    border: 1px solid rgba(217,119,6,0.25);
}
.grants-badges { display: flex; flex-wrap: wrap; }

/* ── Services tab ──────────────────────────────── */
.fg-services-list {
    display: flex;
    flex-direction: column;
    gap: 10px;
}

.fg-service-card {
    padding: 14px;
    border: 1px solid var(--border-default);
    border-left: 3px solid var(--border-default);
    border-radius: var(--radius-sm, 6px);
    transition: border-color 0.2s;
}
.fg-service-card:hover {
    border-left-color: var(--primary-start, #6c80ff);
}

.fg-access-badge {
    display: inline-flex;
    align-items: center;
    padding: 2px 8px;
    border-radius: 4px;
    font-size: 11px;
    font-weight: 600;
    text-transform: uppercase;
    letter-spacing: 0.4px;
}
.fg-access-badge--public {
    background: rgba(72, 187, 120, 0.12);
    color: var(--success-start, #48bb78);
}
.fg-access-badge--platform {
    background: rgba(108, 128, 255, 0.12);
    color: var(--primary-start, #6c80ff);
}

.fg-service-script {
    font-size: 11px;
    color: var(--text-muted);
}

.fg-service-url {
    font-size: 12px;
    color: var(--text-secondary);
    background: var(--bg-card-secondary);
    padding: 2px 6px;
    border-radius: 3px;
}

/* ── Variables tab ──────────────────────────────── */
.fg-var-env-section {
    margin-bottom: 24px;
}

.fg-var-env-header {
    display: flex;
    align-items: center;
    gap: 10px;
    margin-bottom: 10px;
}

.fg-var-env-label {
    display: inline-flex;
    align-items: center;
    padding: 2px 10px;
    border-radius: 4px;
    font-size: 11px;
    font-weight: 700;
    text-transform: uppercase;
    letter-spacing: 0.5px;
}
.fg-var-env-label--dev {
    background: rgba(72, 187, 120, 0.12);
    color: var(--success-start, #48bb78);
}
.fg-var-env-label--live {
    background: rgba(108, 128, 255, 0.12);
    color: var(--primary-start, #6c80ff);
}

.fg-var-list {
    border: 1px solid var(--border-default);
    border-radius: var(--radius-sm, 6px);
    overflow: hidden;
    margin-bottom: 8px;
}

.fg-var-row {
    display: flex;
    align-items: center;
    gap: 8px;
    padding: 8px 12px;
    border-bottom: 1px solid var(--border-default);
    background: var(--bg-card-secondary);
}
.fg-var-row:last-child {
    border-bottom: none;
}

.fg-var-key {
    min-width: 120px;
    font-size: 12px;
    color: var(--text-primary);
    white-space: nowrap;
}

.fg-var-eq {
    color: var(--text-muted);
    font-size: 13px;
    flex-shrink: 0;
}

.fg-var-value {
    flex: 1;
    font-size: 12px;
    font-family: monospace;
}

.fg-var-delete {
    flex-shrink: 0;
    line-height: 1;
}

.fg-var-add {
    display: flex;
    align-items: center;
    gap: 8px;
    padding: 8px 0;
}

.fg-var-add-key {
    width: 160px;
    font-family: monospace;
    font-size: 12px;
}

.fg-var-add-value {
    flex: 1;
    font-family: monospace;
    font-size: 12px;
}

/* ── Transitions ────────────────────────────────── */
.fade-enter-active,
.fade-leave-active {
    transition: opacity 0.2s;
}
.fade-enter,
.fade-leave-to {
    opacity: 0;
}
</style>
